import { Editor, Toolbar, schema as ngxSchema } from 'ngx-editor';
import { MarkSpec, MarkType, NodeSpec, Schema } from 'prosemirror-model';
import { EditorState } from 'prosemirror-state';

const FONT_SIZE_MARK = 'font_size';
const BASE_FONT_SIZE_PX = 15;
const DEFAULT_FONT_SIZE = BASE_FONT_SIZE_PX;
const FONT_SIZE_MIN = 8;
const FONT_SIZE_MAX = 36;

const fontSizeMark: MarkSpec = {
  attrs: {
    size: { default: DEFAULT_FONT_SIZE },
  },
  parseDOM: [
    {
      style: 'font-size',
      getAttrs: (value: string): Record<string, any> | null => {
        const normalized = value.trim().toLowerCase();
        const parsed = Number.parseFloat(normalized);
        if (Number.isNaN(parsed)) {
          return null;
        }

        if (normalized.endsWith('em')) {
          return { size: parsed * BASE_FONT_SIZE_PX };
        }
        if (normalized.endsWith('pt')) {
          return { size: parsed / 0.75 };
        }

        return { size: parsed };
      },
    },
  ],
  toDOM: (mark) => {
    const size = Number(mark.attrs['size']) || DEFAULT_FONT_SIZE;
    const em = Math.round((size / BASE_FONT_SIZE_PX) * 1000) / 1000;
    return ['span', { style: `font-size:${em}em;` }, 0];
  },
};

export const DOCUMENT_TEMPLATE_TOOLBAR: Toolbar = [
  ['bold', 'italic'],
  ['underline', 'strike'],
  ['text_color'],
  ['ordered_list', 'bullet_list'],
  ['align_left', 'align_center', 'align_right', 'align_justify'],
  ['link'],
];

export type DocumentEditorSchemaOptions = {
  nodes?: Record<string, NodeSpec>;
};

export function buildDocumentSchema(options?: DocumentEditorSchemaOptions): Schema {
  const baseNodes = ngxSchema.spec.nodes;
  let nodes = baseNodes;

  if (options?.nodes) {
    Object.entries(options.nodes).forEach(([name, spec]) => {
      nodes = nodes.get(name) ? nodes.update(name, spec) : nodes.addToEnd(name, spec);
    });
  }

  const baseMarks = ngxSchema.spec.marks;
  const marks = baseMarks.get(FONT_SIZE_MARK) ? baseMarks : baseMarks.addToEnd(FONT_SIZE_MARK, fontSizeMark);

  return new Schema({ nodes, marks });
}

export function createDocumentEditor(options?: DocumentEditorSchemaOptions): Editor {
  return new Editor({
    schema: buildDocumentSchema(options),
    inputRules: false,
  });
}

export function applyFontSizeDelta(editor: Editor, delta: number): void {
  const { state, dispatch } = editor.view;
  const markType = state.schema.marks[FONT_SIZE_MARK];

  if (!markType) {
    return;
  }

  const activeSize = getActiveFontSize(state, markType);
  const nextSize = clampFontSize(activeSize + delta);
  applyFontSizeValue(editor, nextSize);
}

export function applyFontSizeValue(editor: Editor, value: number): void {
  const { state, dispatch } = editor.view;
  const markType = state.schema.marks[FONT_SIZE_MARK];

  if (!markType) {
    return;
  }

  const nextSize = clampFontSize(value);
  const { from, to, empty } = state.selection;

  if (empty) {
    dispatch(state.tr.addStoredMark(markType.create({ size: nextSize })));
    return;
  }

  dispatch(state.tr.addMark(from, to, markType.create({ size: nextSize })));
}

function getActiveFontSize(state: EditorState, markType: MarkType): number {
  const marks = state.storedMarks ?? state.selection.$from.marks();
  const mark = marks?.find((item) => item.type === markType);

  return mark?.attrs?.['size'] ?? DEFAULT_FONT_SIZE;
}

function clampFontSize(value: number): number {
  return Math.max(FONT_SIZE_MIN, Math.min(FONT_SIZE_MAX, value));
}
